<template>
    <div class="card">
        <PageHeader
            title="Payroll"
            :show-btn="true"
            @onClickCloseButton="navigateToListPage"
        >
          <template #extra-btn>
            <button type="button" class="head-action-btn" @click="onClickToPrint">
              <i class="fa-solid fa-print"></i>
              Print
            </button>
            <button type="button" class="head-action-btn" @click="exportTable">
              <i class="fa-solid fa-file-excel"></i>
              Export
            </button>
            <FilterPopover
                title="Payroll Filters"
                :active-count="activeFilterCount"
                @open="onOpenFilter"
                @apply="onApplyFilters"
                @clear="onClearFilters"
            >
              <div class="col-12 col-sm-6">
                <label class="filter-label">Branch</label>
                <v-select
                    placeholder="Select Branch"
                    v-model="draftFilters.branch"
                    :options="branchList"
                    label="name"
                />
              </div>
              <div class="col-12 col-sm-6">
                <label class="filter-label">Division</label>
                <v-select
                    placeholder="Select Division"
                    v-model="draftFilters.division"
                    :options="divisionList"
                    label="name"
                />
              </div>
              <div class="col-12 col-sm-6">
                <label class="filter-label">Department</label>
                <v-select
                    placeholder="Select Department"
                    v-model="draftFilters.department"
                    :options="departmentList"
                    label="name"
                />
              </div>
              <div class="col-12 col-sm-6">
                <label class="filter-label">Business Unit</label>
                <v-select
                    placeholder="Select Business Unit"
                    v-model="draftFilters.businessUnit"
                    :options="businessList"
                    label="name"
                />
              </div>
            </FilterPopover>
          </template>
        </PageHeader>

        <div class="col-12 px-2 mt-2 mb-4">
          <div class="card" style="min-height: 200px">
            <div class="table-responsive">
              <table class="table">
                <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Employee Name</th>
                  <th v-for="(item, i) in grossComponentPayHead" :key="i">{{ item.name }}</th>
                  <th>Gross Salary</th>
                  <th v-for="(item, i) in otherEarningPayHead" :key="i">{{ item.name }}</th>
                  <th>Total Earning</th>
                  <th v-for="(item, i) in deductionPayHeadPayHead" :key="i">{{ item.name }}</th>
                  <th>Total Deduction</th>
                  <th>Net Payable</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in payrollData" :key="i">
                  <td>{{ item.serial_no }}</td>
                  <td>{{ item.name }}</td>
                  <td class="text-right" v-for="gh in grossComponentPayHead" :key="gh.id">
                    {{ commaFormat(item.gross_earnings[gh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(item.gross || 0) }}</td>
                  <td class="text-right" v-for="oh in otherEarningPayHead" :key="oh.id">
                    {{ commaFormat(item.other_earnings[oh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(item.total_earning || 0) }}</td>
                  <td class="text-right" v-for="dh in deductionPayHeadPayHead" :key="dh.id">
                    {{ commaFormat(item.deductions[dh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(item.total_deduction || 0) }}</td>
                  <td class="text-right">{{ commaFormat(item.total) }}</td>
                </tr>
                <tr>
                  <td colspan="2">Total</td>
                  <td class="text-right" v-for="gh in grossComponentPayHead" :key="gh.id">
                    {{ commaFormat(summaryData.gross_earnings[gh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(summaryData.total_gross || 0) }}</td>
                  <td class="text-right" v-for="oh in otherEarningPayHead" :key="oh.id">
                    {{ commaFormat(summaryData.other_earnings[oh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(summaryData.total_earning || 0) }}</td>
                  <td class="text-right" v-for="dh in deductionPayHeadPayHead" :key="dh.id">
                    {{ commaFormat(summaryData.deductions[dh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(summaryData.total_deduction || 0) }}</td>
                  <td class="text-right">{{ commaFormat(summaryData.total_net_payable || 0) }}</td>
                </tr>
                </tbody>
              </table>
              <p v-if="payrollData && payrollData.length < 0" class="text-center mt-5">No data</p>
            </div>
          </div>
        </div>
      <div v-if="showReturnForm" class="row px-2 mt-2">
        <div class="col-12 col-md-6 col-lg-4">
          <label for="remarks" class="col-form-label">Remarks</label>
          <textarea
              id="remarks"
              v-model="remarks"
              rows="2"
              placeholder="Enter return remarks"
              class="form-control"
          ></textarea>
        </div>
      </div>
      <div v-if="showSubmitBtn" class="mt-2 d-flex justify-content-start justify-content-sm-end px-2 gap-1 flex-wrap pb-2">
        <button v-if="showReturnBtn && !showReturnForm" :disabled="saveButtonLoader" @click="openReturnForm"
                class="btn btn-danger">
          Return
        </button>
        <button v-if="showReturnForm" :disabled="saveButtonLoader" @click="handleReturn"
                class="btn btn-danger">
          Confirm Return
        </button>
        <button v-if="!showReturnForm" :disabled="saveButtonLoader" @click="handleSubmit"
                class="btn btn-primary">
          Save
        </button>
      </div>

      <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import PageHeader from '@/components/atom/PageHeader';
import {inject, onMounted, reactive, ref} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import FilterPopover from '@/components/atom/FilterPopover'
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import { computed } from 'vue';
import figureFormatter from "@/services/utils/figureFormatter";
import payrollStatementPdf from '@/services/utils/pdf/payrollStatementPdf';
import handleCompany from "@/services/modules/company";
import handleDropdowns from "@/services/modules/dropdowns";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
let saveButtonLoader = ref(false)
const showSubmitBtn = ref(false)
const showReturnForm = ref(false)
const remarks = ref('')
const showReturnBtn = computed(() => route.query.from === 'approval')
const grossComponentPayHead = ref([]);
const otherEarningPayHead = ref([]);
const deductionPayHeadPayHead = ref([]);
const payrollData = ref([]);
const summaryData = ref([]);
const params = ref({
    offset: 20,
});
const getEmptyFilters = () => {
  return {
    branch: null,
    division: null,
    department: null,
    businessUnit: null
  }
}
const appliedFilters = ref(getEmptyFilters())
const draftFilters = ref(getEmptyFilters())
const activeFilterCount = computed(() => {
  return Object.values(appliedFilters.value).filter(function (value) {
    return !! value
  }).length
})
const { commaFormat } = figureFormatter();
const token = computed(() => localStorage.getItem('token'));
let departmentList = ref([])
let branchList = ref([])
let businessList = ref([])
let divisionList = ref([])
const payrollStatementData = ref([]);
const {
  showPayrollProcessData,
  updatePayrollApprovalStatus
} = handleHrPayroll()
const { fetchDropdownList } = handleDropdowns()
const { exportToPDF } = payrollStatementPdf();
const { fetchCompanyInfo } = handleCompany();
let companyInfo = reactive({})

const getPayrollData = async () => {
  let query = payrollQuery();
  let payrollMasterId = route.params.payrollMasterId;
  payrollData.value = []
  otherEarningPayHead.value = []
  grossComponentPayHead.value = []
  deductionPayHeadPayHead.value = []

  await showPayrollProcessData(payrollMasterId, query).then(res => {
    if (!res.status){
      return
    }
    showSubmitBtn.value = res.data.payroll_status == 'draft'
    grossComponentPayHead.value = res.data.components.gross
    otherEarningPayHead.value = res.data.components.earning
    deductionPayHeadPayHead.value = res.data.components.deduction
    payrollData.value = res.data.data;
    summaryData.value = res.data.summary;
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

function payrollQuery() {
  let query = `?company_id=${route.params.companyId}`;
  const filters = appliedFilters.value;
  if (route.query.page) query += '&page=' + route.query.page;
  if (filters.branch) query += '&branch_id=' + filters.branch.id;
  if (filters.division) query += '&division_id=' + filters.division.id;
  if (filters.department) query += '&department_id=' + filters.department.id;
  if (filters.businessUnit) query += '&business_unit_id=' + filters.businessUnit.id;
  query += '&offset=' + params.value.offset
  return query;
}

const onOpenFilter = () => {
  draftFilters.value = Object.assign({}, appliedFilters.value);
}

const onApplyFilters = () => {
  appliedFilters.value = Object.assign({}, draftFilters.value);
  getPayrollData();
}

const onClearFilters = () => {
  appliedFilters.value = getEmptyFilters();
  draftFilters.value = getEmptyFilters();
  getPayrollData();
}

function getQuery() {
  return `?company_id=${route.params.companyId}`;
}

const handleSubmit = async () => {
  saveButtonLoader.value = true
  let query = `?company_id=${route.params.companyId}`;

  let data = {}
  data.current_status = 'draft';
  data.update_status = 'approved';
  data.payroll_master_id = route.params.payrollMasterId;

  await updatePayrollApprovalStatus(data, query).then(res => {
    if (!res.status){
      showError(res.message)
      return
    }

    showSuccess(res.message)
    navigateToListPage()
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    saveButtonLoader.value = false
  })

}

const openReturnForm = () => {
  showReturnForm.value = true
}

const handleReturn = async () => {
  if (!remarks.value) {
    showError('Remarks is required to return the payroll')
    return
  }

  saveButtonLoader.value = true
  let query = `?company_id=${route.params.companyId}`;

  let data = {}
  data.current_status = 'draft';
  data.update_status = 'returned';
  data.payroll_master_id = route.params.payrollMasterId;
  data.remarks = remarks.value;

  await updatePayrollApprovalStatus(data, query).then(res => {
    if (!res.status){
      showError(res.message)
      return
    }
    showSuccess(res.message)
    navigateToListPage()
  }).finally(() => {
    saveButtonLoader.value = false
  })
}

function getListRouteName() {
  if (route.query.from === 'approval') return 'hr-payroll-approval'
  return 'payroll-process'
}

function navigateToListPage() {
  router.push({name: getListRouteName(), params: route.params, query: route.query});
}

async function onClickToPrint() {
  let query = payrollQuery();
  payrollData.value = []
  otherEarningPayHead.value = []
  grossComponentPayHead.value = []
  deductionPayHeadPayHead.value = []
  summaryData.value = []
  payrollStatementData.value = []
  let payrollMasterId = route.params.payrollMasterId;

  await showPayrollProcessData(payrollMasterId, query).then((res) => {
    if (!res.status) {
      return
    }
    grossComponentPayHead.value = res.data.components.gross;
    otherEarningPayHead.value = res.data.components.earning;
    deductionPayHeadPayHead.value = res.data.components.deduction;
    summaryData.value = res.data.summary;
    payrollData.value = res.data.data;
    payrollStatementData.value = res.data.payroll_statement
  }).then(() => {
    exportToPDF(
        companyInfo, grossComponentPayHead.value, otherEarningPayHead.value,
        deductionPayHeadPayHead.value, payrollData.value, summaryData.value, payrollStatementData.value,
    )
  }).catch((err) => {
    console.log("Something went wrong");
  });
}

const exportTable = () => {
  let query = payrollQuery();
  let payrollMasterId = route.params.payrollMasterId;
  let url = `${process.env.VUE_APP_BASE_URL}/api/hr-payroll/payroll/${payrollMasterId}${query}&_token=${token.value}&is_export=1`;
  let a = document.createElement("a");
  a.setAttribute("href", url);
  a.click();
};

const getDropdownList = async () => {
  let query = getQuery();
  query += '&is_branch=1&is_division=1&is_department=1&is_business=1'

  branchList.value = []
  divisionList.value = []
  departmentList.value = []
  businessList.value = []

  await fetchDropdownList(query).then(res => {
    if (!res.status){
      return
    }

    let data = res.data;
    branchList.value = data.branch_list;
    divisionList.value = data.division_list;
    departmentList.value = data.department_list;
    businessList.value = data.business_list;
  }).catch(err => {
    console.log(err)
  })
}

const setCompanyInfo = async () => {
  await fetchCompanyInfo(route.params.companyId).then((res) => {
    companyInfo = res.data;
  }).catch((error) => {
    console.log(error);
  });
}

onMounted(() => {
    Promise.all([
      getPayrollData(),
      getDropdownList(),
      setCompanyInfo()
    ])
})
</script>

<style scoped>
.head-action-btn {
  display: inline-flex;
  align-items: center;
  gap: .4rem;
  height: 36px;
  padding: 0 .9rem;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #475467;
  cursor: pointer;
  transition: all .15s ease;
}
.head-action-btn:hover {
  background: #f9fafb;
  color: #101828;
}
</style>
